import { IFerroviasLista, IPortosLista } from 'interfaces/IAfetacoes';
import { IAtualizarInformacoesEnviar, IAtualizarInformacoesReceber } from 'interfaces/IAtualizarInfo';
import {
  IBalneario,
  IBalnearioComBanhoSalvar,
  IBalnearioInformacoesComBanhoEnviar,
  IBalnearioInformacoesComBanhoReceber,
  IBalnearioInformacoesSemBanhoEnviar,
  IBalnearioInformacoesSemBanhoReceber,
  IBalnearioSemBanhoSalvar,
} from 'interfaces/IBalneario';
import {
  EndpointsOutrosRegimes,
  IBaseCalculoTodas,
  IFatoGerador,
  IRetornoListagemBaseCalculo,
  PaginasOutrosRegimes,
} from 'interfaces/IBasesCalculo';
import { IDeducoes } from 'interfaces/IDeducao';
import {
  INovaGuiaRecolhimento,
  IReceberDadosGuiaRecolhimento,
  IReceberDadosGuiaRecolhimentoResumo,
  IReceberGuiaRecolhimentoConsultaPagamento,
  IReceberGuiaRecolhimentoEmissaoGru,
} from 'interfaces/IGuiaRecolhimento';
import { IMunicipio } from 'interfaces/IMunicipio';
import { INcmListagem } from 'interfaces/INcm';
import { IOutrasBasesNcmEnviar, IOutrasBasesNcmReceber } from 'interfaces/IOutrasBases';
import { IProcessoMinerario, IProcessoMinerarioETipoVinculo } from 'interfaces/IProcessoMinerario';
import { ISubstancia } from 'interfaces/ISubstancia';
import { useApiClient } from '../../../hooks/useApiClient';

export const useBackEndRequests = () => {
  const httpCall = useApiClient();

  const getProcessoMinerarioPorNumero = async (numeroProcesso: string) => {
    const resposta = await httpCall.get<IProcessoMinerario>(`/arrecadacao/api/processo-minerario/${numeroProcesso}`);

    return resposta.data;
  };

  const getListaProcessoPorCpfCnpj = async (cpfCnpj: string, mesExercicio: number, anoExercicio: number) => {
    const resposta = await httpCall.get<IProcessoMinerarioETipoVinculo[]>(
      `/arrecadacao/api/processo-minerario/titular-arrendatario/outros-regimes/${cpfCnpj}/mes-exercicio/${mesExercicio}/ano-exercicio/${anoExercicio}`
    );
    return resposta.data;
  };

  const getNcmPorGuia = async (idGuiaRecolhimento: string) => {
    const resposta = await httpCall.get<INcmListagem[]>(
      `/arrecadacao/api/ncm/por-substancia?guiaRecolhimentoId=${idGuiaRecolhimento}`
    );
    return resposta.data;
  };

  const getMunicipiosPorUf = async (uf: string) => {
    const resposta = await httpCall.get<IMunicipio[]>(`/arrecadacao/api/municipio/uf/${uf}`);
    return resposta.data;
  };

  const getMunicipiosPorFerroviaEUf = async (idFerrovia: number, uf: string) => {
    const resposta = await httpCall.get<IMunicipio[]>(`/arrecadacao/api/municipio/ferrovia/${idFerrovia}/uf/${uf}`);
    return resposta.data;
  };

  const getFerrovias = async () => {
    const resposta = await httpCall.get<IFerroviasLista[]>(`/arrecadacao/api/ferrovia`);
    return resposta.data;
  };

  const getPortos = async () => {
    const resposta = await httpCall.get<IPortosLista[]>(`/arrecadacao/api/porto`);
    return resposta.data;
  };

  const getCanHaveDutovia = async (idGuiaRecolhimento: string) => {
    const resposta = await httpCall.get<boolean>(
      `/arrecadacao/api/guia-recolhimento/base-calculo/afetacao/dutovia/permite/${idGuiaRecolhimento}`
    );
    return resposta.data;
  };

  const getDeducoesBalneario = async (idDaGuiaRecolhimento: string) => {
    const resposta = await httpCall.get<IDeducoes>(
      `/arrecadacao/api/tributos/balneario?idDaGuiaRecolhimento=${idDaGuiaRecolhimento}`
    );
    return resposta.data;
  };

  const getDeducoesOutrasBasesCalculo = async (idDaGuiaRecolhimento: string, idFormacaoBaseCalculo: number) => {
    const resposta = await httpCall.get<IDeducoes>(
      `/arrecadacao/api/tributos/outras-bases-calculo?idDaGuiaRecolhimento=${idDaGuiaRecolhimento}&idFormacaoBaseCalculo=${idFormacaoBaseCalculo}`
    );
    return resposta.data;
  };

  const postGuiaRecolhimento = async (payload: INovaGuiaRecolhimento) => {
    await httpCall
      .post(`/arrecadacao/api/guia-recolhimento`, payload)
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      });
  };

  const putGuiaRecolhimento = async (payload: INovaGuiaRecolhimento) => {
    await httpCall
      .put(`/arrecadacao/api/guia-recolhimento`, payload)
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      });
  };

  const deleteGuiaRecolhimento = async (idGuiaRecolhimento: string) => {
    await httpCall
      .delete(`/arrecadacao/api/guia-recolhimento/${idGuiaRecolhimento}`)
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      });
  };

  const getDadosRecolhimento = async (idGuiaRecolhimento: string) => {
    const resposta = await httpCall.get<IReceberDadosGuiaRecolhimento>(
      `/arrecadacao/api/guia-recolhimento/${idGuiaRecolhimento}`
    );
    return resposta.data;
  };

  const getResumo = async (idGuiaRecolhimento: string) => {
    const resposta = await httpCall.get<IReceberDadosGuiaRecolhimentoResumo>(
      `/arrecadacao/api/guia-recolhimento/resumo/${idGuiaRecolhimento}`
    );
    return resposta.data;
  };

  const getEmitirGru = async (idGuiaRecolhimento: string, formaPagamento: string) => {
    const resposta = await httpCall.get<IReceberGuiaRecolhimentoEmissaoGru>(
      `/arrecadacao/api/guia-recolhimento/emitir-gru/${idGuiaRecolhimento}/forma-pagamento/${formaPagamento}`
    );
    return resposta.data;
  };

  const getConsultarPagamentoGuia = async (idGuiaRecolhimento: string) => {
    const resposta = await httpCall.get<IReceberGuiaRecolhimentoConsultaPagamento>(
      `/arrecadacao/api/guia-recolhimento/consultar-pagamento/${idGuiaRecolhimento}`
    );
    return resposta.data;
  };

  const getFatoGerador = async () => {
    const resposta = await httpCall.get<IFatoGerador[]>(`/arrecadacao/api/guia-recolhimento/base-calculo/fato-gerador`);
    return resposta.data;
  };

  const getListaFormacoesBaseCalculo = async (idGuiaRecolhimento: string, fatoGerador: number) => {
    const resposta = await httpCall.get<IRetornoListagemBaseCalculo[]>(
      `/arrecadacao/api/guia-recolhimento/base-calculo/formacao/${idGuiaRecolhimento}/fato-gerador/${fatoGerador}`
    );
    return resposta.data;
  };

  const postSalvarBalnearioComBanho = async (dados: IBalnearioComBanhoSalvar) => {
    await httpCall
      .post<IBalnearioComBanhoSalvar>(`/arrecadacao/api/guia-recolhimento/balneario/base-calculo/valor-banho`, dados)
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      });
  };

  const postSalvarBalnearioSemBanho = async (dados: IBalnearioSemBanhoSalvar) => {
    await httpCall
      .post<IBalnearioSemBanhoSalvar>(`/arrecadacao/api/guia-recolhimento/balneario/base-calculo/outros-valores`, dados)
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      });
  };

  const putAtualizarBalnearioComBanho = async (dados: IBalnearioComBanhoSalvar) => {
    await httpCall
      .put<IBalnearioComBanhoSalvar>(`/arrecadacao/api/guia-recolhimento/balneario/base-calculo/valor-banho`, dados)
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      });
  };

  const putAtualizarBalnearioSemBanho = async (dados: IBalnearioSemBanhoSalvar) => {
    await httpCall
      .put<IBalnearioSemBanhoSalvar>(`/arrecadacao/api/guia-recolhimento/balneario/base-calculo/outros-valores`, dados)
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      });
  };

  const getBalneario = async (houveBanho: boolean, id: number) => {
    const resposta = await httpCall.get<IBalneario>(
      `/arrecadacao/api/guia-recolhimento/balneario/base-calculo/${houveBanho}/${id}`
    );
    return resposta.data;
  };

  const deleteBalneario = async (houveBanho: boolean, id: number) => {
    await httpCall
      .delete<IBalnearioSemBanhoSalvar>(`/arrecadacao/api/guia-recolhimento/balneario/base-calculo/${houveBanho}/${id}`)
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      });
  };

  const postReceberInformacaoBalnearioSemBanho = async (body: IBalnearioInformacoesSemBanhoEnviar) => {
    const resposta = await httpCall
      .post<IBalnearioInformacoesSemBanhoReceber>(
        `/arrecadacao/api/guia-recolhimento/balneario/base-calculo/outros-valores/buscar-informacoes-base-calculo`,
        body
      )
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      });
    return resposta.data;
  };

  const postReceberInformacaoBalnearioComBanho = async (body: IBalnearioInformacoesComBanhoEnviar) => {
    const resposta = await httpCall
      .post<IBalnearioInformacoesComBanhoReceber>(
        `/arrecadacao/api/guia-recolhimento/balneario/base-calculo/valor-banho/buscar-informacoes-base-calculo`,
        body
      )
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      });
    return resposta.data;
  };

  const getBasesCalculoTodas = async (idGuiaRecolhimento: string) => {
    const resposta = await httpCall.get<IBaseCalculoTodas>(
      `/arrecadacao/api/guia-recolhimento/base-calculo/todas/${idGuiaRecolhimento}`
    );
    return resposta.data;
  };

  const postOutrasBaseDeCalculo = async (guiaDeRecolhimentoId: string, tipoBaseCalculo: PaginasOutrosRegimes) => {
    const resposta = await httpCall
      .post<{ id: number; idGuiaRecolhimento: string }>(
        `/arrecadacao/api/guia-recolhimento/base-calculo/${EndpointsOutrosRegimes[tipoBaseCalculo]}`,
        {
          idGuiaRecolhimento: guiaDeRecolhimentoId,
        }
      )
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      });
    return resposta.data;
  };

  const getNCMsOutrasBasesCalculo = async (idDaBaseDeCalculo: number) => {
    const resposta = await httpCall.get<IOutrasBasesNcmReceber[]>(
      `/arrecadacao/api/guia-recolhimento/base-calculo/itens/${idDaBaseDeCalculo}`
    );
    return resposta.data;
  };

  const getImprimirBoleto = async (idGuiaRecolhimento: string) => {
    const resposta = await httpCall.get<Blob>(
      `/arrecadacao/api/guia-recolhimento/report/boleto-bb/${idGuiaRecolhimento}`,
      {
        responseType: 'blob',
      }
    );
    return resposta.data;
  };

  const postNCMOutrasBasesCalculo = async (body: IOutrasBasesNcmEnviar) => {
    const resposta = await httpCall
      .post<IOutrasBasesNcmReceber>(`/arrecadacao/api/guia-recolhimento/base-calculo/outras/item`, body)
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      });
    return resposta.data;
  };

  const putNCMOutrasBasesCalculo = async (body: IOutrasBasesNcmEnviar, idNCM: number) => {
    const resposta = await httpCall
      .put<IOutrasBasesNcmReceber>(`/arrecadacao/api/guia-recolhimento/base-calculo/outras/item`, {
        ...body,
        id: idNCM,
      })
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      });
    return resposta.data;
  };

  const deleteNCMOutrasBasesCalculo = async (id: number) => {
    await httpCall
      .delete(`/arrecadacao/api/guia-recolhimento/base-calculo/outras/item/${id}`)
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      });
  };

  const deleteOutrasBasesCalculo = async (idBaseCalculo: number) => {
    await httpCall
      .delete(`/arrecadacao/api/guia-recolhimento/base-calculo/${idBaseCalculo}`)
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      });
  };

  const postAtualizarInformacoes = async (body: IAtualizarInformacoesEnviar) => {
    const resposta = await httpCall
      .post<IAtualizarInformacoesReceber>(
        `/arrecadacao/api/guia-recolhimento/base-calculo/outras/item/buscar-informacoes-base-calculo`,
        body
      )
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      });
    return resposta.data;
  };

  const getPermissaoAcessarGuiaRecolhimento = async (idGuiaRecolhimento: string) => {
    const resposta = await httpCall.get(
      `/arrecadacao/api/guia-recolhimento/validar-permissao-guia-recolhimento/${idGuiaRecolhimento}`
    );
    return resposta.status;
  };

  const getProcessosMinerarios = async (cpfCnpj: string) => {
    const resposta = await httpCall.get<string[]>(`/arrecadacao/api/processo-minerario/todos-regimes/${cpfCnpj}`);
    return resposta.data;
  };

  const getSubstancias = async () => {
    const resposta = await httpCall.get<ISubstancia[]>(`/arrecadacao/api/substancia`);
    return resposta.data;
  };

  const getCopiarGuia = async (idGuiaRecolhimento: string) => {
    const resposta = await httpCall.get<string>(`/arrecadacao/api/guia-recolhimento/copiar/${idGuiaRecolhimento}`);
    return resposta.data;
  };

  const getValorMinimoBoleto = async () => {
    const resposta = await httpCall.get<{ valorMinimoBoleto: number }>(
      `/arrecadacao/api/guia-recolhimento/consultar-valor-minimo-boleto`
    );
    return resposta.data;
  };

  return {
    getProcessoMinerarioPorNumero,
    getListaProcessoPorCpfCnpj,
    getNcmPorGuia,
    getMunicipiosPorUf,
    getMunicipiosPorFerroviaEUf,
    getFerrovias,
    getPortos,
    getCanHaveDutovia,
    getDeducoesBalneario,
    getDeducoesOutrasBasesCalculo,
    postGuiaRecolhimento,
    putGuiaRecolhimento,
    deleteGuiaRecolhimento,
    getDadosRecolhimento,
    getResumo,
    getEmitirGru,
    getConsultarPagamentoGuia,
    getFatoGerador,
    getListaFormacoesBaseCalculo,
    postSalvarBalnearioComBanho,
    postSalvarBalnearioSemBanho,
    putAtualizarBalnearioComBanho,
    putAtualizarBalnearioSemBanho,
    getBalneario,
    deleteBalneario,
    postReceberInformacaoBalnearioSemBanho,
    postReceberInformacaoBalnearioComBanho,
    getBasesCalculoTodas,
    postOutrasBaseDeCalculo,
    getNCMsOutrasBasesCalculo,
    postNCMOutrasBasesCalculo,
    putNCMOutrasBasesCalculo,
    deleteNCMOutrasBasesCalculo,
    deleteOutrasBasesCalculo,
    getImprimirBoleto,
    postAtualizarInformacoes,
    getPermissaoAcessarGuiaRecolhimento,
    getProcessosMinerarios,
    getSubstancias,
    getCopiarGuia,
    getValorMinimoBoleto,
  };
};
